import { useEffect, useMemo, useRef, useState } from "react";
import { endpoints } from "@/lib/api";
import { Loader2, CheckCircle2, AlertCircle, RotateCw, Zap, ArrowDown, Send, FileText, Eye, Download, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// ============================================================================
// Agent step flow — Collect · Resolve · Reason · Decide · Hand off
// ============================================================================
// 5 vertical steps that light up in order when the user hits Run agent.
// Collect shows a manager-friendly card list on the main area with a narrow
// dark engineer terminal pinned on the right ("Under the hood"), dual-density
// so leaders see plain English + raw endpoint in the same row.
// ============================================================================

const STEPS = [
  { key: "collect",  title: "Collect",   subtitle: "pulling enterprise inputs" },
  { key: "resolve",  title: "Resolve",   subtitle: "values the agent will reason over" },
  { key: "reason",   title: "Reason",    subtitle: "step-by-step trace" },
  { key: "decide",   title: "Decide",    subtitle: "settled equation + total" },
  { key: "handoff",  title: "Hand off",  subtitle: "structured payload for next agent" },
];

const STUMBLE_WAIT_HEAD = 300;   // ms before 401 appears
const STUMBLE_RETRY_TAIL = 400;  // ms after 200 success for the trailing newline

export function AgentPhaseFlow({
  hop, s, c,
  effectiveDecision, effectiveReasoning, effectiveHandoff,
  canEdit, editableIndices, stageOverrides, hasOverrides, onOverride,
  onSendHandoff, onOpenDocs, docCount,
  forecastLink, brandBlue, brandSoft,
  running, settled,
  planPanel,  // optional JSX — if provided, rendered as a leading "Plan" step
  hasPlanPhase,       // true for Refresh / Demand / Recovery — split triggers
  onConnectSources,   // starts Phase 1 (Collect) when the user clicks the CTA
}) {
  const [inputs, setInputs] = useState(null);
  const [pullLog, setPullLog] = useState([]);  // array of {state, connector, ...}
  const [phase1Done, setPhase1Done] = useState(false);
  const stageRef = useRef(hop.stage);

  // Fetch connector config once per stage.
  useEffect(() => {
    stageRef.current = hop.stage;
    endpoints.agentInputs(hop.stage).then(setInputs).catch(() => setInputs(null));
    setPullLog([]);
    setPhase1Done(false);
  }, [hop.stage]);

  // Phase 1 animation: play connector pulls sequentially whenever `running`
  // flips from false → true. Cancel/reset if stage changes mid-run.
  const runToken = useRef(0);
  useEffect(() => {
    if (!running || !inputs) return;
    const token = ++runToken.current;
    (async () => {
      setPullLog([]);
      setPhase1Done(false);
      for (let i = 0; i < inputs.connectors.length; i++) {
        const conn = inputs.connectors[i];
        if (token !== runToken.current || stageRef.current !== hop.stage) return;
        // emit "in-flight"
        setPullLog((prev) => [...prev, { ...conn, idx: i, state: "pending" }]);
        if (conn.stumble) {
          await sleep(STUMBLE_WAIT_HEAD);
          if (token !== runToken.current) return;
          setPullLog((prev) => prev.map((p, j) => j === i ? { ...p, state: "stumble" } : p));
          await sleep(conn.stumble_delay_ms || 1400);
          if (token !== runToken.current) return;
          setPullLog((prev) => prev.map((p, j) => j === i ? { ...p, state: "retry" } : p));
          await sleep(STUMBLE_RETRY_TAIL);
          if (token !== runToken.current) return;
          setPullLog((prev) => prev.map((p, j) => j === i ? { ...p, state: "ok" } : p));
        } else {
          await sleep(conn.latency_ms);
          if (token !== runToken.current) return;
          setPullLog((prev) => prev.map((p, j) => j === i ? { ...p, state: "ok" } : p));
        }
        await sleep(140);  // small gap between pulls
      }
      if (token === runToken.current) setPhase1Done(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, inputs, hop.stage]);

  const resolvedReady = phase1Done || s.inboxRevealed > 0 || settled;
  const reasoningReady = resolvedReady && (s.reasoningRevealed > 0 || settled);
  const decisionReady  = s.decisionTermsRevealed > 0 || settled;
  const handoffReady   = s.outboxRevealed > 0 || s.handoffSent;

  return (
    <div className="space-y-4">
      <Step
        verb="Collect"
        subtitle={inputs ? `${inputs.connectors.length} enterprise connectors` : "preparing stream…"}
        icon="download"
        active={running || phase1Done}
        done={phase1Done}
        brandBlue={brandBlue}
      >
        <CollectBlock
          connectors={inputs?.connectors || []}
          log={pullLog}
          running={running}
          phase1Done={phase1Done}
          hasPlanPhase={hasPlanPhase}
          onConnectSources={onConnectSources}
          agentStatus={s.status}
          brandBlue={brandBlue}
          brandSoft={brandSoft}
        />
        {/* Demand agent only — ad-hoc intake tile from ServiceNow */}
        {hop.stage === "plan" && <AdhocIntakeTile brandBlue={brandBlue} brandSoft={brandSoft} agentStatus={s.status} phase1Done={phase1Done} />}
      </Step>

      {planPanel && (
        <Step
          verb="Plan"
          subtitle="Scenario Planner · connect historical sources to simulate"
          icon="chart"
          active={true}
          done={true}
          brandBlue={brandBlue}
        >
          {planPanel}
        </Step>
      )}

      <Step
        verb="Reason"
        subtitle="step-by-step trace"
        icon="sparkles"
        active={reasoningReady}
        done={s.reasoningRevealed >= (effectiveReasoning?.length || 0) && (effectiveReasoning?.length || 0) > 0}
        brandBlue={brandBlue}
      >
        {reasoningReady ? (
          <ReasoningList steps={effectiveReasoning} revealed={s.reasoningRevealed} running={running} brandBlue={brandBlue} />
        ) : (
          <Dimmed text="Waiting for the Collect pulls to finish." />
        )}
      </Step>

      <Step
        verb="Resolve"
        subtitle="values the agent reasoned over"
        icon="filter"
        active={resolvedReady}
        done={resolvedReady}
        brandBlue={brandBlue}
      >
        {resolvedReady ? (
          <ResolvedInputs
            inputs={inputs}
            editable={canEdit && editableIndices.length > 0}
            editableIndices={editableIndices}
            stageOverrides={stageOverrides}
            onOverride={onOverride}
            decisionTerms={c.decision?.terms || []}
            forecastLink={forecastLink}
            brandBlue={brandBlue}
            brandSoft={brandSoft}
          />
        ) : (
          <Dimmed text="Waiting for reasoning to settle." />
        )}
      </Step>

      <Step
        verb="Decide"
        subtitle="input flow · click a number to drill into devices"
        icon="check"
        active={decisionReady}
        done={settled}
        brandBlue={brandBlue}
      >
        {decisionReady ? (
          <DecisionBlock
            stage={hop.stage}
            decision={effectiveDecision}
            termsRevealed={s.decisionTermsRevealed}
            settled={settled}
            forecastLink={forecastLink}
            brandBlue={brandBlue}
          />
        ) : (
          <Dimmed text="Reasoning must settle first." />
        )}
      </Step>

      <Step
        verb={`Hand off → ${c.handoff?.target_label || "next agent"}${c.handoff?.secondary ? ` + ${c.handoff.secondary.target_label}` : ""}`}
        subtitle="structured payload · downloadable"
        icon="send"
        active={handoffReady}
        done={s.handoffSent}
        brandBlue={brandBlue}
      >
        {handoffReady ? (
          <HandoffBlock
            stage={hop.stage}
            handoff={effectiveHandoff}
            decision={effectiveDecision}
            revealed={s.outboxRevealed}
            sent={s.handoffSent}
            status={s.status}
            onSend={onSendHandoff}
            onOpenDocs={onOpenDocs}
            docCount={docCount}
            brandBlue={brandBlue}
            brandSoft={brandSoft}
          />
        ) : (
          <Dimmed text="Decision must settle before handoff." />
        )}
      </Step>
    </div>
  );
}


// ============================================================================
// Step shell (verb-only header, icon badge)
// ============================================================================
import { Download as DownloadIcon, Filter, Sparkles as SparklesIcon, Check as CheckIcon, BarChart3 } from "lucide-react";
const STEP_ICONS = {
  download: DownloadIcon,
  filter: Filter,
  sparkles: SparklesIcon,
  check: CheckIcon,
  send: Send,
  chart: BarChart3,
};
function Step({ verb, subtitle, icon, active, done, brandBlue, children }) {
  const Ico = STEP_ICONS[icon] || DownloadIcon;
  return (
    <div
      className="rounded-xl transition-all"
      style={{
        border: `1px solid ${active ? `${brandBlue}33` : "#E2E8F0"}`,
        backgroundColor: active ? "#FFFFFF" : "#F8FAFC",
      }}
    >
      <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: active ? `${brandBlue}1A` : "transparent" }}>
        <div
          className="w-7 h-7 rounded-full grid place-items-center shrink-0 transition-colors"
          style={{
            backgroundColor: done ? brandBlue : (active ? `${brandBlue}22` : "#E2E8F0"),
            color: done ? "#FFFFFF" : (active ? brandBlue : "#94A3B8"),
          }}
        >
          {done ? <CheckCircle2 size={14} /> : <Ico size={13} />}
        </div>
        <div className="flex-1 min-w-0">
          <span
            className="text-[14px] font-semibold"
            style={{ color: active ? "#0F172A" : "#94A3B8" }}
          >
            {verb}
          </span>
          <span className="text-[11px] text-slate-500 ml-2">· {subtitle}</span>
        </div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}


function Dimmed({ text }) {
  return <div className="text-[11px] text-slate-400 italic">{text}</div>;
}


// ============================================================================
// Collect step · manager-friendly rows + narrow engineer terminal on the side
// ============================================================================
function CollectBlock({ connectors, log, running, phase1Done, hasPlanPhase, onConnectSources, agentStatus, brandBlue, brandSoft }) {
  const showPreview = !running && log.length === 0 && !phase1Done;
  // Build the merged "manager row" view: either preview (dim) or the live log
  // enriched with connector friendly labels.
  const rows = showPreview
    ? connectors.map((c, i) => ({ ...c, idx: i, state: "queued" }))
    : log;

  // For Plan-equipped stages (Refresh / Demand / Recovery) we gate the step
  // behind an explicit "Connect sources" button so the two-phase story reads
  // as: Collect connects first → Plan runs the reasoning + chart.
  const showConnectCTA = hasPlanPhase && !running && !phase1Done && agentStatus !== "ready" && agentStatus !== "passed";

  return (
    <div className="space-y-3" data-testid="phase1-terminal">
      {showConnectCTA && (
        <div
          className="rounded-lg p-3 flex items-center gap-3 flex-wrap"
          style={{ border: `1px dashed ${brandBlue}44`, backgroundColor: "#F8FBFF" }}
          data-testid="collect-connect-cta"
        >
          <div
            className="w-8 h-8 rounded-md grid place-items-center shrink-0"
            style={{ backgroundColor: `${brandBlue}14`, color: brandBlue }}
          >
            <Zap size={14} />
          </div>
          <div className="leading-tight flex-1 min-w-[200px]">
            <div className="text-[11.5px] font-semibold text-slate-900">
              Sources not yet connected
            </div>
            <div className="text-[11px] text-slate-500">
              Click Connect sources to run the {connectors.length || 0} enterprise connectors. Each tile lights up green when its pull returns.
            </div>
          </div>
          <Button
            onClick={onConnectSources}
            className="text-white hover:opacity-90 h-9 px-3 shrink-0"
            style={{ backgroundColor: brandBlue, animation: "pulseConnect 2s infinite" }}
            data-testid="collect-connect-sources-btn"
          >
            <Zap size={12} className="mr-1.5" />
            Connect sources
          </Button>
          <style>{`@keyframes pulseConnect { 0%,100%{box-shadow:0 0 0 0 ${brandBlue}44;} 50%{box-shadow:0 0 0 6px ${brandBlue}00;} }`}</style>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* MAIN · manager-friendly cards (dual-density) */}
        <div className="lg:col-span-3 space-y-2" data-testid="collect-manager-view">
          {rows.map((row, i) => (
            <ManagerRow key={i} row={row} brandBlue={brandBlue} />
          ))}
          {rows.length === 0 && (
            <div className="text-[12px] text-slate-500 italic px-1">loading connector list…</div>
          )}
          {running && !phase1Done && rows.length > 0 && (
            <div className="text-[11px] text-slate-400 italic flex items-center gap-1.5 pt-1">
              <Loader2 size={11} className="animate-spin" /> streaming…
            </div>
          )}
        </div>

        {/* SIDE · dark engineer terminal (under the hood) */}
        <div className="lg:col-span-2" data-testid="phase1-log">
          <div
            className="rounded-lg font-mono text-[11px] leading-5 overflow-hidden h-full"
            style={{ backgroundColor: "#0B1220", color: "#CBD5E1" }}
          >
            <div className="px-3 py-2 flex items-center gap-2 border-b" style={{ borderColor: "#1E293B", backgroundColor: "#0F172A" }}>
              <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "#EF4444" }} />
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "#F59E0B" }} />
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "#10B981" }} />
              </div>
              <span className="text-[10px] text-slate-400 ml-2">under the hood · raw log</span>
            </div>
            <div className="p-3 min-h-[150px]">
              {rows.map((entry, i) => (
                <TerminalRow key={i} entry={entry} />
              ))}
              {running && !phase1Done && (
                <div className="animate-pulse" style={{ color: "#CBD5E1" }}>▌</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManagerRow({ row, brandBlue }) {
  const state = row.state || "queued";
  const dot = {
    queued:  { bg: "#CBD5E1", icon: null, label: "queued" },
    pending: { bg: "#60A5FA", icon: <Loader2 size={11} className="animate-spin" />, label: "reading…" },
    stumble: { bg: "#F59E0B", icon: <AlertCircle size={11} />, label: row.stumble_friendly || row.stumble_reason || "retrying…" },
    retry:   { bg: "#F59E0B", icon: <RotateCw size={11} />,    label: "recovered" },
    ok:      { bg: "#10B981", icon: <CheckCircle2 size={11} />,label: "complete" },
  }[state];

  const friendly = row.friendly || `Reading from ${row.system}`;
  const summary = state === "ok"
    ? `${row.rows?.toLocaleString?.()} records · ${row.latency_ms} ms`
    : null;

  return (
    <div
      className="rounded-lg border px-3 py-2.5 transition-all"
      style={{
        borderColor: state === "ok" ? "#D1FAE5" : state === "stumble" || state === "retry" ? "#FDE68A" : "#E2E8F0",
        backgroundColor: state === "ok" ? "#F0FDF4" : state === "stumble" || state === "retry" ? "#FFFBEB" : "#FFFFFF",
        opacity: state === "queued" ? 0.55 : 1,
      }}
    >
      <div className="flex items-start gap-3">
        {/* System badge */}
        <div
          className="w-8 h-8 rounded-md grid place-items-center text-[10px] font-bold text-white shrink-0"
          style={{ backgroundColor: row.color || "#64748B" }}
        >
          {row.icon}
        </div>
        {/* Friendly text (main) */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{row.system}</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold" style={{ color: dot.bg }}>
              {dot.icon}
              <span className="uppercase tracking-wider">{dot.label}</span>
            </span>
          </div>
          <div className="text-[13px] text-slate-900 mt-0.5 leading-5">{friendly}</div>
          <div className="text-[10.5px] text-slate-500 mt-0.5 font-mono truncate">{row.endpoint}</div>
        </div>
        {/* Summary (right) */}
        {summary && (
          <div className="text-right shrink-0">
            <div className="text-[12px] font-bold text-slate-900 tabular-nums">{row.rows?.toLocaleString?.()}</div>
            <div className="text-[10px] text-slate-500">records · {row.latency_ms} ms</div>
          </div>
        )}
      </div>
    </div>
  );
}

function TerminalRow({ entry }) {
  const prefix = {
    queued:  <span className="text-slate-600">·</span>,
    pending: <span style={{ color: "#CBD5E1" }}>▸</span>,
    stumble: <span style={{ color: "#F59E0B" }}>✗</span>,
    retry:   <span style={{ color: "#F59E0B" }}>↻</span>,
    ok:      <span style={{ color: "#10B981" }}>✓</span>,
  }[entry.state] || <span className="text-slate-500">·</span>;
  const dim = entry.state === "queued";
  return (
    <div className="flex items-start gap-2 flex-wrap" style={{ opacity: dim ? 0.5 : 1 }}>
      <div className="w-3 text-center shrink-0">{prefix}</div>
      <span className="text-slate-300">
        <span className="font-bold" style={{ color: "#93C5FD" }}>{entry.method}</span>
        {" "}
        <span className="text-slate-200">{entry.endpoint}</span>
      </span>
      {entry.state === "ok" && (
        <span className="ml-auto text-[11px] text-slate-400 whitespace-nowrap">
          {entry.rows.toLocaleString()} · {entry.latency_ms}ms
        </span>
      )}
      {entry.state === "pending" && (
        <span className="ml-auto text-[10px] text-slate-500 whitespace-nowrap flex items-center gap-1">
          <Loader2 size={10} className="animate-spin" /> in-flight
        </span>
      )}
      {entry.state === "stumble" && (
        <span className="ml-auto text-[11px] whitespace-nowrap" style={{ color: "#F59E0B" }}>401</span>
      )}
      {entry.state === "retry" && (
        <span className="ml-auto text-[11px] whitespace-nowrap" style={{ color: "#F59E0B" }}>200 OK</span>
      )}
      {entry.state === "queued" && (
        <span className="ml-auto text-[10px] text-slate-500 whitespace-nowrap">[queued]</span>
      )}
    </div>
  );
}


// ============================================================================
// Phase 2 · Resolved inputs
// ============================================================================
function ResolvedInputs({ inputs, editable, editableIndices, stageOverrides, onOverride, decisionTerms, forecastLink, brandBlue, brandSoft }) {
  const tiles = inputs?.resolved || [];
  // Build a map of editable inbox indices → tile indices (best-effort by order).
  // We render tiles in the backend-defined order. If an inbox slot is editable
  // and matches by position, surface an inline editor.
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
      {tiles.map((t, i) => {
        const linked = t.from_forecast;
        // info_only tiles (e.g. Workday planned headcount) never bind to a decision term.
        const termPos = i - tiles.slice(0, i).filter((x) => x.info_only).length;
        const editableIdx = editable && !t.info_only ? editableIndices[termPos] : undefined;
        const term = editableIdx != null ? decisionTerms[editableIdx] : null;
        const overrideVal = editableIdx != null ? stageOverrides[editableIdx] : null;
        return (
          <div
            key={i}
            className="rounded-lg px-3 py-2.5 border transition-all"
            style={{
              borderColor: linked ? `${brandBlue}44` : "#E2E8F0",
              backgroundColor: linked ? `${brandBlue}0A` : "#FFFFFF",
            }}
            data-testid={`resolved-input-${i}`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[9.5px] font-bold uppercase tracking-wider" style={{ color: linked ? brandBlue : "#64748B" }}>
                {t.label}
              </span>
              {linked && (
                <span className="text-[9px] font-mono text-slate-400">↑</span>
              )}
            </div>
            {editableIdx != null && term ? (
              <div className="flex items-center gap-1">
                <span className="text-[16px] font-bold" style={{ color: brandBlue }}>
                  {term.sign === "−" ? "−" : "+"}
                </span>
                <input
                  type="number"
                  value={overrideVal ?? term.value}
                  onChange={(e) => onOverride(editableIdx, e.target.value)}
                  className="w-16 text-[16px] font-bold text-slate-900 bg-transparent border-b outline-none"
                  style={{ borderColor: "#FDE68A" }}
                />
              </div>
            ) : (
              <div className="text-[16px] font-bold text-slate-900">{t.value}</div>
            )}
            <div className="text-[10px] text-slate-500 mt-0.5">{t.detail}</div>
          </div>
        );
      })}
      {forecastLink && !tiles.some((t) => t.from_forecast) && (
        <div
          className="rounded-lg px-3 py-2.5 border"
          style={{ borderColor: `${brandBlue}44`, backgroundColor: `${brandBlue}0A` }}
        >
          <div className="text-[9.5px] font-bold uppercase tracking-wider" style={{ color: brandBlue }}>Forecast link</div>
          <div className="text-[16px] font-bold text-slate-900">P50 {forecastLink.p50}</div>
          <div className="text-[10px] text-slate-500">P10 {forecastLink.p10} · P90 {forecastLink.p90}</div>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// Phase 3 · Reasoning
// ============================================================================
function ReasoningList({ steps, revealed, running, brandBlue }) {
  return (
    <ol className="space-y-2 font-mono text-[12.5px] text-slate-700" data-testid="phase3-reasoning">
      {steps.map((step, i) => (
        <li
          key={i}
          className="flex gap-3 transition-all"
          style={{
            opacity: i < revealed ? 1 : 0.18,
            transform: i < revealed ? "translateX(0)" : "translateX(-4px)",
          }}
        >
          <span className="text-[11px] text-slate-400 w-5 shrink-0 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
          <span className="leading-5">{step}</span>
        </li>
      ))}
      {running && revealed < steps.length && (
        <li className="pl-8">
          <span className="animate-pulse" style={{ color: brandBlue }}>▌</span>
        </li>
      )}
    </ol>
  );
}


// ============================================================================
// Phase 4 · Decision — "input flow" diagram with clickable device drill
// ============================================================================
// Replaces the old "117 + 54 + 29 = 386" equation line. Each term renders as
// a card chip showing its connector label + value; clicking the value opens a
// device-level drill popup. Chips are joined by + / − / → connectors so the
// math reads as a flow instead of an equation.
// ============================================================================
function DecisionBlock({ stage, decision, termsRevealed, settled, forecastLink, brandBlue }) {
  const [pulseKey, setPulseKey] = useState(0);
  const [drill, setDrill] = useState(null);  // {termIndex, label, value}
  const [adhocCount, setAdhocCount] = useState(null);
  const prevP50 = useRef(null);
  useEffect(() => {
    if (!forecastLink) return;
    if (prevP50.current !== null && prevP50.current !== forecastLink.p50) {
      setPulseKey((k) => k + 1);
    }
    prevP50.current = forecastLink.p50;
  }, [forecastLink?.p50]);
  // Demand agent — surface ad-hoc intake count as an extra input chip.
  useEffect(() => {
    if (stage !== "plan") return;
    endpoints.demandAdhocIntake().then((d) => setAdhocCount(d.count)).catch(() => {});
  }, [stage]);
  if (!decision) return <Dimmed text="No decision configured." />;
  const terms = decision.terms || [];
  const revealed = terms.slice(0, termsRevealed);

  const openDrill = (i, t) => setDrill({ termIndex: i, label: t.label, value: t.value, sign: t.sign });

  return (
    <div data-testid="phase4-decision">
      {forecastLink && (
        <div
          key={pulseKey}
          className="mb-3 rounded-lg px-3 py-2 flex items-center gap-3 flex-wrap"
          style={{
            border: `1px solid ${brandBlue}33`,
            backgroundColor: `${brandBlue}0A`,
            animation: pulseKey > 0 ? "forecastPulse 650ms ease-out" : undefined,
          }}
        >
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: brandBlue }} />
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: brandBlue }}>Linked to forecast above</span>
          </div>
          <span className="text-[11px] font-mono text-slate-700">
            <span className="font-bold" style={{ color: brandBlue }}>P50 {forecastLink.p50}</span>
            <span className="text-slate-400 mx-1.5">·</span>P10 {forecastLink.p10}
            <span className="text-slate-400 mx-1.5">·</span>P90 {forecastLink.p90}
            <span className="text-slate-400 mx-1.5">·</span>{forecastLink.confidence}% conf
          </span>
          <span className="text-[10px] text-slate-500 italic ml-auto">drag a slider above · pulses</span>
        </div>
      )}

      {/* Input-flow diagram */}
      <div className="flex items-stretch gap-2 flex-wrap">
        {revealed.map((t, i) => {
          const isMinus = t.sign === "−";
          const isInfo = t.sign === "•";
          const connector = i === 0
            ? null
            : isMinus
            ? <span className="shrink-0 self-center text-slate-400 text-lg font-light px-0.5">−</span>
            : isInfo
            ? <ArrowRight size={14} className="shrink-0 self-center text-slate-300" />
            : <span className="shrink-0 self-center text-slate-400 text-lg font-light px-0.5">+</span>;
          // After the first term (Workday joiners) for the Demand agent,
          // slot in the ad-hoc count chip so the math reads clearly as
          //    570 joiners + N ad-hoc + 186 refresh − 34 − 54 → 668.
          const insertAdhocHere = stage === "plan" && settled && i === 0 && adhocCount != null;
          return (
            <>
              <div key={i} className="flex items-stretch gap-2" style={{ animation: "slideIn 300ms ease-out both" }}>
                {connector}
                <TermChip
                  term={t}
                  index={i}
                  isMinus={isMinus}
                  isInfo={isInfo}
                  onClick={() => openDrill(i, t)}
                  brandBlue={brandBlue}
                />
              </div>
              {insertAdhocHere && (
                <div key={`adhoc-${i}`} className="flex items-stretch gap-2" style={{ animation: "slideIn 300ms ease-out both" }}>
                  <span className="shrink-0 self-center text-slate-400 text-lg font-light px-0.5">+</span>
                  <div
                    className="rounded-lg px-3 py-2 border text-left"
                    style={{ borderColor: `${brandBlue}44`, backgroundColor: `${brandBlue}08` }}
                    data-testid="decision-term-adhoc"
                  >
                    <div className="text-[9.5px] font-bold uppercase tracking-wider mb-0.5" style={{ color: brandBlue }}>
                      Ad-hoc requests
                    </div>
                    <div className="flex items-end gap-1.5">
                      <span className="text-xl font-bold tabular-nums text-slate-900">{adhocCount}</span>
                      <Zap size={10} className="mb-1 opacity-70" style={{ color: brandBlue }} />
                    </div>
                    <div className="text-[9.5px] text-slate-400 mt-0.5">ServiceNow + manual</div>
                  </div>
                </div>
              )}
            </>
          );
        })}

        {settled && (
          <>
            <ArrowRight size={16} className="shrink-0 self-center text-slate-400" style={{ animation: "slideIn 300ms ease-out both" }} />
            <button
              onClick={() => openDrill(-1, { label: decision.total_label, value: decision.total_value, sign: "+" })}
              className="group rounded-lg px-4 py-2.5 border-2 transition-all hover:shadow-md text-left"
              style={{
                borderColor: brandBlue,
                backgroundColor: `${brandBlue}0E`,
                animation: pulseKey > 0
                  ? "settleIn 500ms ease-out both, forecastPulse 650ms ease-out"
                  : "settleIn 500ms ease-out both",
              }}
              data-testid="decision-total-chip"
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: brandBlue }} />
                <span className="text-[9.5px] font-bold uppercase tracking-wider" style={{ color: brandBlue }}>
                  {decision.total_label}
                </span>
              </div>
              <div className="flex items-end gap-1.5">
                <span className="text-2xl font-bold tabular-nums" style={{ color: brandBlue }} data-testid="decision-total">
                  {decision.total_value.toLocaleString()}
                </span>
                <Eye size={12} className="mb-1.5 opacity-50 group-hover:opacity-100 transition-opacity" style={{ color: brandBlue }} />
              </div>
              <div className="text-[9.5px] text-slate-500 mt-0.5">{decision.total_unit || "devices"} · click to preview</div>
            </button>
          </>
        )}
      </div>

      {settled && decision.sub_breakdown && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 mt-3 border-t border-slate-100">
          {decision.sub_breakdown.map((b) => (
            <div key={b.label}>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{b.label}</div>
              <div className="text-sm font-bold text-slate-900">{b.value}</div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes slideIn   { from{opacity:0;transform:translateX(-6px);} to{opacity:1;transform:translateX(0);} }
        @keyframes settleIn  { from{opacity:0;transform:scale(0.9);} to{opacity:1;transform:scale(1);} }
        @keyframes forecastPulse { 0%{box-shadow:0 0 0 0 ${brandBlue}66;} 70%{box-shadow:0 0 0 10px ${brandBlue}00;} 100%{box-shadow:0 0 0 0 ${brandBlue}00;} }
      `}</style>

      <DeviceDrillDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        stage={stage}
        termIndex={drill?.termIndex ?? 0}
        label={drill?.label}
        value={drill?.value}
        brandBlue={brandBlue}
      />
    </div>
  );
}

// Per-term chip. Compact card with uppercase label + big value + eye icon.
function TermChip({ term, index, isMinus, isInfo, onClick, brandBlue }) {
  return (
    <button
      onClick={onClick}
      className="group rounded-lg px-3 py-2 border transition-all hover:shadow-sm text-left"
      style={{
        borderColor: isInfo ? "#E2E8F0" : isMinus ? "#FECACA" : "#DBEAFE",
        backgroundColor: "#FFFFFF",
      }}
      data-testid={`decision-term-${index}`}
    >
      <div className="text-[9.5px] font-bold uppercase tracking-wider text-slate-600 mb-0.5">
        {term.label}
      </div>
      <div className="flex items-end gap-1.5">
        <span className="text-xl font-bold tabular-nums text-slate-900">
          {isMinus && <span className="text-slate-400">−</span>}
          {term.value.toLocaleString()}
        </span>
        <Eye size={11} className="mb-1 opacity-40 group-hover:opacity-100 transition-opacity text-slate-500" />
      </div>
      <div className="text-[9.5px] text-slate-400 mt-0.5">click to preview</div>
    </button>
  );
}

// -------- CSV helper --------
function toCsv(columns, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = columns.join(",");
  const body = rows.map((r) => columns.map((c) => esc(r[c])).join(",")).join("\n");
  return `${head}\n${body}\n`;
}

function triggerDownload(filename, mime, text) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Device drill popup — opens on term click. Shows up to 50 sample rows + full
// cohort count + Download CSV button. Backed by /lifecycle/decision-sample.
function DeviceDrillDialog({ open, onClose, stage, termIndex, label, value, brandBlue }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || termIndex < 0) {
      setData(null);
      return;
    }
    setLoading(true);
    endpoints.decisionSample(stage, termIndex, 50)
      .then((d) => setData(d))
      .catch(() => setData({ rows: [], columns: [], total: value, shown: 0 }))
      .finally(() => setLoading(false));
  }, [open, stage, termIndex, value]);

  // Total-chip case (termIndex = -1): aggregate rows across all terms would be
  // the ideal payload but we settle for a simple message pointing at individual
  // term drills — keeps the popup fast + aligned with the demo narrative.
  const isTotal = termIndex < 0;

  const handleDownload = () => {
    if (!data) return;
    const csv = toCsv(data.columns, data.rows);
    triggerDownload(`${stage}_${label}_${data.shown}of${data.total}.csv`, "text/csv", csv);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="decision-drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${brandBlue}1A`, color: brandBlue }}>
              {stage}
            </span>
            <span className="text-slate-900">{label}</span>
            <span className="text-slate-400">·</span>
            <span className="tabular-nums font-bold" style={{ color: brandBlue }}>{(value || 0).toLocaleString()}</span>
            <span className="text-[12px] text-slate-500 font-normal">devices</span>
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            {isTotal
              ? "This is the composite output of the agent. Download the full cohort below."
              : "Device-level evidence behind this number. Click a term chip to drill in."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto rounded-lg border border-slate-200">
          {loading && (
            <div className="p-6 text-center text-slate-500 text-sm flex items-center gap-2 justify-center">
              <Loader2 size={14} className="animate-spin" /> loading device records…
            </div>
          )}
          {!loading && data && data.rows.length > 0 && (
            <table className="w-full text-[11.5px]" data-testid="device-table">
              <thead className="sticky top-0 bg-slate-50">
                <tr>
                  {data.columns.map((c) => (
                    <th key={c} className="text-left px-2.5 py-2 font-bold uppercase tracking-wider text-slate-500 text-[9.5px] border-b border-slate-200 whitespace-nowrap">
                      {c.replace(/_/g, " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60">
                    {data.columns.map((c) => (
                      <td key={c} className="px-2.5 py-1.5 text-slate-700 whitespace-nowrap font-mono">
                        {r[c] == null ? "—" : String(r[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && data && data.rows.length === 0 && (
            <div className="p-6 text-center text-slate-500 text-sm">No backing records.</div>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 text-[11.5px] text-slate-600">
          <span>
            {data ? (
              <>Showing <b className="text-slate-900 tabular-nums">{data.shown.toLocaleString()}</b> of <b className="text-slate-900 tabular-nums">{data.total.toLocaleString()}</b> records</>
            ) : "…"}
          </span>
          <Button
            onClick={handleDownload}
            disabled={!data || data.rows.length === 0}
            size="sm"
            className="text-white hover:opacity-90"
            style={{ backgroundColor: brandBlue }}
            data-testid="download-devices-csv-btn"
          >
            <Download size={12} className="mr-1.5" />
            Download sample · CSV
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Phase 5 · Handoff — JSON preview + CSV/JSON download + visual flyer on send
// ============================================================================
// Compact JSON-style payload card used by HandoffBlock for both primary and
// secondary (parallel) handoff targets. Secondary cards get a subtle outline
// so the dual-handoff story reads clearly ("Demand" + "Recovery").
function PayloadCard({ label, fields, revealed, flying, brandBlue, secondary = false }) {
  return (
    <div className="relative">
      <div
        className="absolute -top-2.5 left-3 px-1.5 text-[9.5px] font-bold uppercase tracking-wider rounded-sm z-10"
        style={{
          backgroundColor: secondary ? "#F8FBFF" : brandBlue,
          color: secondary ? brandBlue : "#fff",
          border: secondary ? `1px solid ${brandBlue}44` : "none",
        }}
      >
        → {label}
      </div>
      <div
        className="rounded-md font-mono text-[12px] p-3 transition-all"
        style={{
          backgroundColor: "#0B1220",
          color: "#CBD5E1",
          border: secondary ? `1px dashed ${brandBlue}44` : "none",
          transform: flying
            ? (secondary
                ? "translate(140px, 160px) scale(0.4)"   // flyer dives down-right toward Recovery
                : "translate(140px, -160px) scale(0.4)") // flyer up-right toward next-stage
            : "translate(0,0) scale(1)",
          opacity: flying ? 0.2 : 1,
          transition: "transform 650ms cubic-bezier(0.4, 0.0, 0.2, 1), opacity 650ms ease-out",
        }}
      >
        <div className="text-slate-500">{"{"}</div>
        {fields.slice(0, revealed).map((f, i) => (
          <div key={i} className="pl-4" style={{ animation: "slideIn 300ms ease-out both" }}>
            <span style={{ color: "#93C5FD" }}>"{f.key}"</span>
            <span className="text-slate-500">: </span>
            <span style={{ color: "#86EFAC" }}>"{f.value}"</span>
            {i < fields.length - 1 && <span className="text-slate-500">,</span>}
          </div>
        ))}
        <div className="text-slate-500">{"}"}</div>
      </div>
    </div>
  );
}

function HandoffBlock({ stage, handoff, decision, revealed, sent, status, onSend, onOpenDocs, docCount, brandBlue, brandSoft }) {
  const [flying, setFlying] = useState(false);
  const fields = handoff?.fields || [];
  const secondary = handoff?.secondary || null; // optional parallel payload (e.g. Refresh → Recovery)
  const secondaryFields = secondary?.fields || [];
  // Join the two targets into a single "A + B" label when a parallel handoff
  // exists so the Send button + flyer read as one coordinated commit.
  const combinedLabel = secondary
    ? `${handoff?.target_label} + ${secondary.target_label}`
    : handoff?.target_label;

  const payloadObj = useMemo(() => {
    const primary = {
      from_stage: stage,
      to_stage: handoff?.target,
      to_label: handoff?.target_label,
      payload: Object.fromEntries(fields.map((f) => [f.key, f.value])),
    };
    if (!secondary) return primary;
    return {
      ...primary,
      parallel: {
        to_stage: secondary.target,
        to_label: secondary.target_label,
        payload: Object.fromEntries(secondaryFields.map((f) => [f.key, f.value])),
      },
    };
  }, [handoff, stage, fields, secondary, secondaryFields]);

  if (!handoff) return <Dimmed text="No handoff configured." />;

  const handleDownloadJson = () => {
    triggerDownload(`${stage}_handoff.json`, "application/json", JSON.stringify(payloadObj, null, 2));
  };

  const handleDownloadCsv = async () => {
    // Aggregate device rows across all decision terms into a single CSV with
    // a leading `cohort` column so the next team can slice by refresh action.
    if (!decision?.terms?.length) {
      // Fallback — just dump the handoff fields.
      const cols = ["key", "value"];
      const rows = fields.map((f) => ({ key: f.key, value: f.value }));
      triggerDownload(`${stage}_handoff.csv`, "text/csv", toCsv(cols, rows));
      return;
    }
    const all = [];
    let cols = null;
    for (let i = 0; i < decision.terms.length; i++) {
      try {
        const d = await endpoints.decisionSample(stage, i, 50);
        if (!cols) cols = ["cohort", ...d.columns];
        d.rows.forEach((r) => all.push({ cohort: d.term_label, ...r }));
      } catch (e) { /* skip failing term */ }
    }
    if (!cols || all.length === 0) {
      triggerDownload(`${stage}_handoff.csv`, "text/csv", "cohort\n");
      return;
    }
    triggerDownload(`${stage}_handoff_sample.csv`, "text/csv", toCsv(cols, all));
  };

  const handleSend = () => {
    // Visual flyer: scale/translate the payload card toward the next agent in
    // the rail (rail sits above the workbench, so the chip animates up-right).
    setFlying(true);
    setTimeout(() => {
      setFlying(false);
      onSend?.();
    }, 650);
  };

  return (
    <div data-testid="phase5-handoff" className="relative">
      <div className={secondary ? "grid grid-cols-1 lg:grid-cols-2 gap-3" : ""}>
        {/* Primary payload → first target */}
        <PayloadCard
          label={handoff.target_label}
          fields={fields}
          revealed={revealed}
          flying={flying}
          brandBlue={brandBlue}
        />
        {/* Secondary payload → second target (parallel) */}
        {secondary && (
          <PayloadCard
            label={secondary.target_label}
            fields={secondaryFields}
            revealed={revealed >= fields.length ? secondaryFields.length : Math.max(0, revealed - fields.length / 2)}
            flying={flying}
            brandBlue={brandBlue}
            secondary
          />
        )}
      </div>

      {/* Trailing "cued → NextAgent(s)" pulse during flyer */}
      {flying && (
        <div
          className="absolute right-4 top-1/2 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
          style={{ color: brandBlue, animation: "slideIn 420ms ease-out both" }}
        >
          <ArrowRight size={12} />
          transmitting → {combinedLabel}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        {!sent && revealed >= fields.length && status !== "running" && (
          <Button
            onClick={handleSend}
            size="sm"
            className="text-white hover:opacity-90"
            style={{ backgroundColor: brandBlue }}
            data-testid="send-handoff-btn"
          >
            <Send size={11} className="mr-1.5" />
            Send handoff → {combinedLabel}
          </Button>
        )}
        {sent && (
          <span className="text-[11px] font-semibold flex items-center gap-1.5" style={{ color: brandBlue }}>
            <CheckCircle2 size={12} /> Handoff emitted · {combinedLabel} received
          </span>
        )}

        {/* Downloads (always available after fields reveal) */}
        {revealed >= fields.length && (
          <>
            <button
              onClick={handleDownloadCsv}
              className="text-[11px] font-semibold h-8 px-2.5 rounded-md flex items-center gap-1.5 transition-colors border border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="download-handoff-csv-btn"
            >
              <Download size={11} />
              Output · CSV
            </button>
            <button
              onClick={handleDownloadJson}
              className="text-[11px] font-semibold h-8 px-2.5 rounded-md flex items-center gap-1.5 transition-colors border border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="download-handoff-json-btn"
            >
              <FileText size={11} />
              Output · JSON
            </button>
          </>
        )}

        {docCount > 0 && (
          <button
            data-testid={`show-docs-btn-${handoff.target_label || "x"}`}
            onClick={onOpenDocs}
            className="ml-auto text-[11px] font-semibold h-8 px-2.5 rounded-md flex items-center gap-1.5 transition-colors hover:opacity-90"
            style={{ backgroundColor: brandSoft, color: brandBlue, border: `1px dashed ${brandBlue}44` }}
          >
            <FileText size={11} />
            Show docs · {docCount}
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Demand agent · Ad-hoc intake tile
// ============================================================================
// ServiceNow-sourced open device requests pre-populated into the Collect step.
// Admins can defer any request from this run or add a manual entry; the count
// flows into the Decide math as an additional input chip.
function AdhocIntakeTile({ brandBlue, brandSoft, agentStatus, phase1Done }) {
  const [data, setData] = useState(null);
  const [deferred, setDeferred] = useState({});
  const [adding, setAdding] = useState(false);
  const [manualName, setManualName] = useState("");

  // Only fetch the ServiceNow intake AFTER the Collect step connectors have
  // returned. Pre-collect the tile reads as a locked placeholder so the ALM
  // sees the ad-hoc stream is derived from the connect — not fabricated.
  useEffect(() => {
    if (!phase1Done) return;
    endpoints.demandAdhocIntake().then(setData).catch(() => {});
  }, [phase1Done]);

  if (!phase1Done) {
    return (
      <div
        className="mt-4 rounded-xl border-2 border-dashed p-5 flex flex-col items-center text-center gap-2"
        style={{ borderColor: `${brandBlue}44`, backgroundColor: "#F8FBFF" }}
        data-testid="demand-adhoc-locked"
      >
        <div
          className="w-10 h-10 rounded-full grid place-items-center"
          style={{ backgroundColor: `${brandBlue}14`, color: brandBlue }}
        >
          <Zap size={14} />
        </div>
        <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500">
          Ad-hoc requests · pending
        </div>
        <div className="text-[12px] text-slate-600 max-w-md">
          ServiceNow device-request intake will populate once <b>Connect sources</b> returns above. The ALM can defer or add manual entries before Run agent.
        </div>
      </div>
    );
  }

  const toggleDefer = (id) => setDeferred((d) => ({ ...d, [id]: !d[id] }));
  const addManual = () => {
    if (!manualName.trim() || !data) return;
    const next = {
      req_id: `ADH-M-${Date.now()}`,
      requester: manualName.trim(),
      business_unit: "Manual · added by Admin",
      role: "—",
      device_profile: "Std · Dell Latitude 5440",
      requested_delivery: "2026-12-15",
      ticket_id: "—",
      priority: "P2",
      source: "Admin manual entry",
    };
    setData({ count: (data.count || 0) + 1, requests: [...(data.requests || []), next] });
    setManualName("");
    setAdding(false);
  };

  const active = (data?.requests || []).filter((r) => !deferred[r.req_id]);

  return (
    <div className="mt-4 rounded-xl p-4" style={{ border: `1px dashed ${brandBlue}44`, backgroundColor: "#F8FBFF" }} data-testid="demand-adhoc-tile">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold flex items-center gap-1.5" style={{ color: brandBlue }}>
            <Zap size={12} /> Ad-hoc requests · ServiceNow
          </div>
          <div className="text-[12px] text-slate-700 mt-0.5">
            <b className="tabular-nums">{active.length}</b> active · <b className="tabular-nums">{Object.values(deferred).filter(Boolean).length}</b> deferred · pre-populated from last 30 days of device tickets. Flows into Decide as an additional input chip.
          </div>
        </div>
        {!adding ? (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)} className="h-8 text-[11px]" data-testid="adhoc-add-btn">
            + Add manual
          </Button>
        ) : (
          <div className="flex items-center gap-1.5">
            <input
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              placeholder="Requester name"
              className="h-8 px-2 text-[11px] border border-slate-200 rounded-md"
              onKeyDown={(e) => e.key === "Enter" && addManual()}
            />
            <Button size="sm" onClick={addManual} className="h-8 text-[11px] text-white" style={{ backgroundColor: brandBlue }}>Add</Button>
            <button onClick={() => setAdding(false)} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
          </div>
        )}
      </div>

      <div className="max-h-[220px] overflow-y-auto space-y-1.5">
        {(data?.requests || []).map((r) => {
          const isDeferred = !!deferred[r.req_id];
          return (
            <div
              key={r.req_id}
              className="rounded-md px-3 py-2 flex items-center gap-2 text-[11.5px] transition-opacity"
              style={{
                backgroundColor: "#FFF",
                border: `1px solid ${brandSoft}`,
                opacity: isDeferred ? 0.45 : 1,
              }}
            >
              <span className="text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded-sm shrink-0" style={{ backgroundColor: r.priority === "P1" ? "#DC262622" : r.priority === "P2" ? "#F59E0B22" : "#60A5FA22", color: r.priority === "P1" ? "#DC2626" : r.priority === "P2" ? "#F59E0B" : "#2563EB" }}>{r.priority}</span>
              <span className="font-semibold text-slate-900 shrink-0 min-w-[140px]">{r.requester}</span>
              <span className="text-slate-500 truncate flex-1">{r.business_unit} · {r.role}</span>
              <span className="text-slate-500 shrink-0 hidden md:inline">{r.device_profile}</span>
              <span className="font-mono text-[10px] text-slate-400 shrink-0">{r.ticket_id}</span>
              <button
                onClick={() => toggleDefer(r.req_id)}
                className="text-[10px] font-semibold px-2 h-6 rounded-sm shrink-0 transition-colors"
                style={{ color: isDeferred ? brandBlue : "#64748B", border: `1px solid ${isDeferred ? brandBlue : "#E2E8F0"}` }}
                data-testid={`adhoc-defer-${r.req_id}`}
              >
                {isDeferred ? "Restore" : "Defer"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// util
// ============================================================================
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
